/**
 * Spec 160 — le client affiché : UN résolveur, appelé par la coquille avant tout rendu de page.
 *
 * ## Ce qu'il ferme
 *
 * Avant la spec, le client vivait en mémoire de la page : un rechargement ramenait au « premier » client du
 * périmètre, dont l'ordre n'est pas stable (la liste arrive du serveur sans tri), et le bandeau recalculait son propre
 * client à côté de celui de la page. On pouvait lire les chiffres d'un client en croyant en lire un autre.
 *
 * ## Les règles (research D-160-02 à D-160-05)
 *
 * - Le **périmètre** est celui de `selectableAccounts(profile)` et de rien d'autre : la règle d'exclusion du compte
 *   revendeur ne se recopie pas (`.claude/rules/reseller-scope.md`, sentinelle `accountScope.scope.test.ts`). L'appelant
 *   le passe tel quel ; ce module ne lit jamais `profile.accounts`.
 * - Une **adresse invalide refuse**, elle ne se rabat jamais sur un autre client. Forme d'un GUID d'abord : pour un
 *   administrateur, `assertTenantAccess` laisse passer tout identifiant, et une valeur quelconque irait jusqu'à SQL.
 * - Sans client dans l'adresse : le client courant de l'onglet (une navigation interne garde son client), puis le
 *   dernier client consulté, puis le premier par ordre alphabétique — jamais l'ordre reçu du serveur.
 */
import type { AccountRef } from '../types/profile'
import { getSolutionRoutes } from './solutionRegistry'

/** Paramètre d'adresse qui porte le client affiché (contracts/adresse-client.md). */
export const PARAM_CLIENT = 'client'
/** Paramètre d'adresse de la Vue client (`vue=client`). */
export const PARAM_VUE = 'vue'
export const VALEUR_VUE_CLIENT = 'client'

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export type SourceClient = 'adresse' | 'onglet' | 'dernier' | 'premier'

export type ClientResolu =
  | { kind: 'client'; account: AccountRef; source: SourceClient }
  /** Adresse hors périmètre, inexistante ou mal formée : même issue, rien ne dit laquelle (invariant 6a). */
  | { kind: 'refus' }
  /** Périmètre vide et aucune adresse : l'état vide de la coquille, inchangé. */
  | { kind: 'aucun' }

export interface EntreesResolution {
  /** `selectableAccounts(profile)` — seule source du périmètre. */
  perimetre: readonly AccountRef[]
  /** Valeur du paramètre `client` de l'adresse ; `null` s'il est absent. */
  depuisAdresse: string | null
  /** La page montre-t-elle les données d'un client ? (`estPageClient`) */
  pageClient: boolean
  /** Client courant de l'onglet, tenu par la coquille d'une navigation à l'autre. */
  ongletCourant: string | null
  /** Dernier client consulté par cet utilisateur sur ce navigateur. */
  dernierConsulte: string | null
}

function chercher(perimetre: readonly AccountRef[], id: string | null): AccountRef | undefined {
  if (!id || !GUID.test(id)) return undefined
  const cle = id.toLowerCase()
  return perimetre.find(a => a.accountId.toLowerCase() === cle)
}

export function resoudreClientAffiche(e: EntreesResolution): ClientResolu {
  if (e.pageClient && e.depuisAdresse !== null) {
    const designe = chercher(e.perimetre, e.depuisAdresse)
    return designe ? { kind: 'client', account: designe, source: 'adresse' } : { kind: 'refus' }
  }
  const onglet = chercher(e.perimetre, e.ongletCourant)
  if (onglet) return { kind: 'client', account: onglet, source: 'onglet' }
  const dernier = chercher(e.perimetre, e.dernierConsulte)
  if (dernier) return { kind: 'client', account: dernier, source: 'dernier' }

  // Tri d'une COPIE : hors revendeur, `selectableAccounts` rend `profile.accounts` lui-même.
  const [premier] = [...e.perimetre].sort((x, y) => x.displayName.localeCompare(y.displayName, 'fr', { sensitivity: 'base' }))
  return premier ? { kind: 'client', account: premier, source: 'premier' } : { kind: 'aucun' }
}

/** Pages client de la plateforme. Celles des solutions se lisent au registre, sans en nommer aucune (P5). */
const PAGES_CLIENT_PLATEFORME = ['overview', 'alertes']

function correspond(motif: string, segments: string[]): boolean {
  const attendus = motif.split('/').filter(Boolean)
  if (attendus.length !== segments.length) return false
  return attendus.every((s, i) => s.startsWith(':') ? (segments[i] ?? '').length > 0 : s === segments[i])
}

/**
 * La page montre-t-elle les données d'un client ? (research D-160-05) Toute route déclarée par une solution en montre
 * aujourd'hui ; une solution qui aurait une page sans client demandera un champ au registre, à ce moment-là.
 */
export function estPageClient(pathname: string, routesSolutions: readonly string[] = getSolutionRoutes().map(r => r.path)): boolean {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return false
  return [...PAGES_CLIENT_PLATEFORME, ...routesSolutions].some(m => correspond(m, segments))
}

/** Le chemin donné, désignant le client (et la Vue client si demandée) ; les autres paramètres et l'ancre restent. */
export function avecClient(chemin: string, accountId: string, vueClient = false): string {
  const url = new URL(chemin, 'http://portail.local')
  url.searchParams.set(PARAM_CLIENT, accountId)
  if (vueClient) url.searchParams.set(PARAM_VUE, VALEUR_VUE_CLIENT)
  else url.searchParams.delete(PARAM_VUE)
  return `${url.pathname}${url.search}${url.hash}`
}

const cleDernierClient = (userId: string) => `pragmelis365.dernierClient.${userId}`

/** Dernier client consulté (D-160-04) : par utilisateur, l'identifiant seul ; relu puis validé par le résolveur. */
export function lireDernierClient(userId: string): string | null {
  try {
    return localStorage.getItem(cleDernierClient(userId))
  } catch {
    return null // stockage indisponible : on retombe sur le premier par nom
  }
}

export function ecrireDernierClient(userId: string, accountId: string): void {
  try {
    localStorage.setItem(cleDernierClient(userId), accountId)
  } catch {
    // stockage plein ou interdit : la préférence est perdue, rien d'autre
  }
}
